import * as React from 'react';
import Loader from '../common/Loader';
import { Switch, Route, useLocation } from 'react-router-dom';
import { StylesProps } from '../theme/jss-types';
import SideMenu from './side-menu';
import OTPLogin from 'src/auth/Login';
import EmailLogin from 'src/auth/EmailLogin';
import ForgotPassword from 'src/auth/ForgotPassword';
import Logout from 'src/auth/Logout';
import ResetPassword from 'src/auth/ResetPassword';
import VehicleRequests from 'src/components/pages/VehicleRequests/VehicleRequests';
import AssignedVehicles from 'src/components/pages/AssignedVehicles/AssignedVehicles';
import AssignedDrivers from 'src/components/pages/AssignedDrivers/AssignedDrivers';
import MoveableAssets from 'src/components/pages/MoveableAssets/MoveableAssets';
import {
  vehicleRequestsRoute,
  vehicleRequestsMMRoute,
  loginRoute,
  loginViaOtpRoute,
  logoutRoute,
  tripDetailsRoute,
  istDetailsRoute,
  consignmentsRoute,
  analyticsRoute,
  forgotPasswordRoute,
  resetPassword,
  assignedVehiclesRoute,
  assignedDriversRoute,
  moveableAssetsRoute,
  invoicesRoute,
  receivablesRoute,
  downloadRequestRoute,
  userManagementRoute,
} from 'src/routing/routeConstants';
import authHelper from 'src/auth/auth-helper';
import TripDetails from 'src/components/pages/TripDetails/TripDetails';
import DeliveryAcknowledgement from 'src/components/pages/Delivery/DeliveryAcknowledgement';
import Analytics from 'src/components/analytics/analytics';
import VehicleRequestsIST from 'src/components/pages/VehicleRequests/VehicleRequestsIST';
import ISTDetails from 'src/components/pages/TripDetails/ISTDetails';
import InvoiceDashboard from 'src/components/pages/Invoices/InvoiceDashboard';
import ReceivablesContainer from 'src/components/pages/Receivables/ReceivablesContainer';
import DownloadRequests from 'src/components/pages/DownloadRequests/DownloadRequests';
import UserManagement from 'src/components/pages/UserManagement/UserManagement';
import { connect } from 'react-redux';
import ProtectedRoute from './ProtectedRoute';
import { getRequiredInvoiceConfig } from 'src/components/pages/Invoices/Invoices.utils';
import CustomFrontendHost from 'src/components/custom-frontend/CustomFrontendHost';
import { getCustomFrontendMenu } from 'src/components/custom-frontend/customFrontendManifest';

const { Suspense } = React;

interface IProps {
  consignmentActionConfig?: any;
  showVehicleRequestsMM?: boolean;
  vendorData?: any;
}

const Routing = (props: IProps) => {
  const {
    consignmentActionConfig, showVehicleRequestsMM, vendorData,
  } = props;

  const requiredConfig = getRequiredInvoiceConfig(
    consignmentActionConfig?.flow_config,
    { vendorData },
  );

  const showConsignmentInvoice = requiredConfig?.show_consignment_invoice || false;
  const location = useLocation();

  const [isAuthenticated, setIsAuthenticated] = React.useState(authHelper.isAuthenticated());
  React.useEffect(() => {
    // Re-check auth status on every location change
    setIsAuthenticated(authHelper.isAuthenticated());
  }, [location.pathname]);

  return (
    <Suspense fallback={<Loader />}>
      <React.Suspense fallback={<Loader />}>
        <Switch>
          <ProtectedRoute
            exact
            path={resetPassword}
            component={(props) => <ResetPassword {...props} checkToken />}
          />
          {isAuthenticated ? (
            <>
              <ProtectedRoute exact path={vehicleRequestsRoute} component={VehicleRequests} />
              <ProtectedRoute
                exact
                path={vehicleRequestsMMRoute}
                component={VehicleRequestsIST}
                isAllowed={showVehicleRequestsMM}
              />
              <ProtectedRoute exact path={assignedVehiclesRoute} component={AssignedVehicles} />
              <ProtectedRoute exact path={assignedDriversRoute} component={AssignedDrivers} />
              <ProtectedRoute
                exact
                path={moveableAssetsRoute}
                component={MoveableAssets}
              />
              <ProtectedRoute exact path={logoutRoute} component={Logout} />
              <ProtectedRoute exact path={consignmentsRoute} component={DeliveryAcknowledgement} />
              <ProtectedRoute exact path={receivablesRoute} component={ReceivablesContainer} />
              <ProtectedRoute exact path={downloadRequestRoute} component={DownloadRequests} />
              <ProtectedRoute path={analyticsRoute} component={Analytics} />
              <ProtectedRoute exact path={userManagementRoute} component={UserManagement} />
              <ProtectedRoute path={tripDetailsRoute} component={TripDetails} />
              <ProtectedRoute path={istDetailsRoute} component={ISTDetails} />
              <ProtectedRoute
                path={invoicesRoute}
                component={InvoiceDashboard}
                isAllowed={showConsignmentInvoice}
              />
              {getCustomFrontendMenu()
                .filter((m) => m.target === 'inApp')
                .map((m) => (
                  <ProtectedRoute
                    exact
                    key={m.id}
                    path={m.iframeRoute}
                    component={() => <CustomFrontendHost menuEntry={m} />}
                  />
                ))}
            </>
          ) : (
            <>
              <ProtectedRoute exact path={loginRoute} component={EmailLogin} />
              <ProtectedRoute exact path={loginViaOtpRoute} component={OTPLogin} />
              <ProtectedRoute exact path={forgotPasswordRoute} component={ForgotPassword} />
            </>
          )}
        </Switch>
      </React.Suspense>
      <SideMenu />
    </Suspense>
  );
};

const mapStateToProps = (state) => {
  const {
    vendor_config,
    vendor_data,
  } = state.masterData;
  return {
    consignmentActionConfig: vendor_config?.consignment_action_config,
    showVehicleRequestsMM: vendor_config?.show_vehicle_requests_mm || false,
    vendorData: vendor_data,
  };
};

export default connect(mapStateToProps)(Routing);
